import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { ar } from "date-fns/locale";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart,
} from "recharts";

interface Props {
  childId: string;
  childName?: string;
}

export default function PerformanceTrendChart({ childId, childName }: Props) {
  const now = new Date();
  const months = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = subMonths(now, 5 - i);
      return {
        date: d,
        start: startOfMonth(d),
        end: endOfMonth(d),
        label: format(d, "MMM", { locale: ar }),
        fullLabel: format(d, "MMMM yyyy", { locale: ar }),
      };
    });
  }, []);

  const dateRange = {
    start: format(months[0].start, "yyyy-MM-dd"),
    end: format(months[5].end, "yyyy-MM-dd"),
  };

  const { data: attendance, isLoading: attLoading } = useQuery({
    queryKey: ["trend-attendance", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("attendance")
        .select("date, status")
        .eq("attendee_id", childId)
        .eq("attendee_type", "child")
        .gte("date", dateRange.start)
        .lte("date", dateRange.end);
      return data || [];
    },
    enabled: !!childId,
  });

  const { data: exams, isLoading: exLoading } = useQuery({
    queryKey: ["trend-exams", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("exam_submissions")
        .select("submitted_at, percentage")
        .eq("child_id", childId)
        .eq("status", "completed")
        .gte("submitted_at", months[0].start.toISOString())
        .lte("submitted_at", months[5].end.toISOString());
      return data || [];
    },
    enabled: !!childId,
  });

  const { data: games, isLoading: gmLoading } = useQuery({
    queryKey: ["trend-games", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("game_results")
        .select("played_at, percentage")
        .eq("child_id", childId)
        .gte("played_at", months[0].start.toISOString())
        .lte("played_at", months[5].end.toISOString());
      return data || [];
    },
    enabled: !!childId,
  });

  const isLoading = attLoading || exLoading || gmLoading;

  const chartData = useMemo(() => {
    return months.map((m) => {
      const mStart = m.start;
      const mEnd = m.end;

      // Attendance rate
      const monthAtt = (attendance || []).filter((a: any) => {
        const d = new Date(a.date);
        return d >= mStart && d <= mEnd;
      });
      const present = monthAtt.filter((a: any) => a.status === "present" || a.status === "late").length;
      const totalDays = monthAtt.length;
      const attRate = totalDays > 0 ? Math.round((present / totalDays) * 100) : null;

      // Exam avg
      const monthExams = (exams || []).filter((e: any) => {
        const d = new Date(e.submitted_at);
        return d >= mStart && d <= mEnd;
      });
      const examAvg = monthExams.length > 0
        ? Math.round(monthExams.reduce((s: number, e: any) => s + (e.percentage || 0), 0) / monthExams.length)
        : null;

      // Game avg
      const monthGames = (games || []).filter((g: any) => {
        const d = new Date(g.played_at);
        return d >= mStart && d <= mEnd;
      });
      const gameAvg = monthGames.length > 0
        ? Math.round(monthGames.reduce((s: number, g: any) => s + (g.percentage || 0), 0) / monthGames.length)
        : null;

      return {
        month: m.label,
        fullMonth: m.fullLabel,
        الحضور: attRate,
        الامتحانات: examAvg,
        الألعاب: gameAvg,
      };
    });
  }, [attendance, exams, games, months]);

  const hasData = chartData.some(
    (d) => d.الحضور !== null || d.الامتحانات !== null || d.الألعاب !== null
  );

  if (isLoading) {
    return (
      <Card className="shadow-card">
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          تطور الأداء خلال 6 أشهر
          {childName && <span className="text-muted-foreground font-normal">- {childName}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <p className="text-xs text-muted-foreground text-center py-8">لا توجد بيانات كافية لعرض الرسم البياني</p>
        ) : (
          <div className="h-[280px] w-full" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorAtt" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorExam" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorGame" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" unit="%" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                    direction: "rtl",
                  }}
                  formatter={(value: any, name: string) => [value !== null ? `${value}%` : "—", name]}
                  labelFormatter={(label: string) => {
                    const item = chartData.find(d => d.month === label);
                    return item?.fullMonth || label;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, direction: "rtl" }} />
                <Area
                  type="monotone" dataKey="الحضور" stroke="hsl(var(--primary))" strokeWidth={2}
                  fill="url(#colorAtt)" connectNulls dot={{ r: 3 }} activeDot={{ r: 5 }}
                />
                <Area
                  type="monotone" dataKey="الامتحانات" stroke="#f59e0b" strokeWidth={2}
                  fill="url(#colorExam)" connectNulls dot={{ r: 3 }} activeDot={{ r: 5 }}
                />
                <Area
                  type="monotone" dataKey="الألعاب" stroke="#8b5cf6" strokeWidth={2}
                  fill="url(#colorGame)" connectNulls dot={{ r: 3 }} activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
